import {
  HTTPMethods,
  RequestExecutor,
} from "../../api-helpers/RequestExecutor";
import { getGrowthEndpoints } from "../../config";
import { captureErrorToSentry } from "../../utilities/sentryHelper";
import { FirebaseService } from "../../services/firebase/FirebaseService";
import {
  GetFeaturesListResponse,
  GetFeatureByIdResponse,
  CreateFeatureRequest,
  CreateFeatureResponse,
  UpdateFeatureRequest,
  UpdateFeatureResponse,
  DeleteFeatureResponse,
  BulkUploadFeaturesResponse,
  DownloadFeaturesListResponse,
} from "../models/serviceFeatures/ServiceScreenFeatureModel";

export class ServiceScreenFeaturesService {
  private async getToken(): Promise<string> {
    const token = await FirebaseService.fetchIdToken();
    if (!token) {
      throw new Error("No authentication token available");
    }
    return token;
  }

  async getFeaturesList(isExternal: boolean = false): Promise<GetFeaturesListResponse> {
    try {
      const token = await this.getToken();
      const path = `${getGrowthEndpoints()}/serviceScreenFeaturesApiFunctions-getFeaturesList`;
      const res = (await RequestExecutor.fetch({
        apiPath: path,
        apiMethod: HTTPMethods.POST,
        requestHeader: {
          Authorization: token,
        },
        requestBody: { isExternal },
      })) as any;

      if (res.error || res.status !== 200) {
        throw new Error(res.error || "Failed to fetch features list");
      }

      // RequestExecutor returns { data: apiResponse, status, error }
      // API response structure: { status: 200, success: true, data: { features, totalCount, ... } }
      const apiResponse = res.data;
      
      // Handle both direct API response and wrapped response
      if (apiResponse && apiResponse.success && apiResponse.data) {
        return {
          status: apiResponse.status || 200,
          response: apiResponse.data,
        } as GetFeaturesListResponse;
      }
      
      // Fallback: if data is already in the expected format
      if (apiResponse && apiResponse.features) {
        return {
          status: apiResponse.status || 200,
          response: apiResponse,
        } as GetFeaturesListResponse;
      }
      
      throw new Error("Invalid response format");
    } catch (error) {
      captureErrorToSentry(error, "Error in getFeaturesList");
      throw error;
    }
  }

  async getFeatureById(featureId: string): Promise<GetFeatureByIdResponse> {
    try {
      const token = await this.getToken();
      const path = `${getGrowthEndpoints()}/serviceScreenFeaturesApiFunctions-getFeatureById`;
      const res = (await RequestExecutor.fetch({
        apiPath: path,
        apiMethod: HTTPMethods.POST,
        requestHeader: {
          Authorization: token,
        },
        requestBody: {
          featureId,
        },
      })) as any;

      if (res.error || res.status !== 200) {
        throw new Error(res.error || "Failed to fetch feature");
      }

      // RequestExecutor returns { data: apiResponse, status, error }
      // API response structure: { status: 200, success: true, data: { feature } }
      const apiResponse = res.data;
      
      if (apiResponse && apiResponse.success && apiResponse.data) {
        return {
          status: apiResponse.status || 200,
          response: apiResponse.data,
        } as GetFeatureByIdResponse;
      }
      
      // Fallback: if data is already in the expected format
      if (apiResponse && apiResponse.feature) {
        return {
          status: apiResponse.status || 200,
          response: apiResponse,
        } as GetFeatureByIdResponse;
      }
      
      throw new Error("Invalid response format");
    } catch (error) {
      captureErrorToSentry(error, "Error in getFeatureById");
      throw error;
    }
  }

  async createFeature(
    featureData: Omit<CreateFeatureRequest, "token">
  ): Promise<CreateFeatureResponse> {
    try {
      const token = await this.getToken();
      const path = `${getGrowthEndpoints()}/serviceScreenFeaturesApiFunctions-createFeature`;
      const res = (await RequestExecutor.fetch({
        apiPath: path,
        apiMethod: HTTPMethods.POST,
        requestHeader: {
          Authorization: token,
        },
        requestBody: {
          ...featureData,
        },
      })) as any;

      if (res.error || res.status !== 200) {
        throw new Error(res.error || "Failed to create feature");
      }

      // RequestExecutor returns { data: apiResponse, status, error }
      // API response structure: { status: 200, success: true, data: { feature } }
      const apiResponse = res.data;
      
      if (apiResponse && apiResponse.success && apiResponse.data) {
        return {
          status: apiResponse.status || 200,
          response: apiResponse.data,
        } as CreateFeatureResponse;
      }
      
      // Fallback: if data is already in the expected format
      if (apiResponse && apiResponse.feature) {
        return {
          status: apiResponse.status || 200,
          response: apiResponse,
        } as CreateFeatureResponse;
      }
      
      throw new Error("Invalid response format");
    } catch (error) {
      captureErrorToSentry(error, "Error in createFeature");
      throw error;
    }
  }

  async updateFeature(
    featureId: string,
    featureData: Omit<UpdateFeatureRequest, "token" | "featureId">
  ): Promise<UpdateFeatureResponse> {
    try {
      const token = await this.getToken();
      const path = `${getGrowthEndpoints()}/serviceScreenFeaturesApiFunctions-updateFeature`;
      const res = (await RequestExecutor.fetch({
        apiPath: path,
        apiMethod: HTTPMethods.POST,
        requestHeader: {
          Authorization: token,
        },
        requestBody: {
          featureId,
          ...featureData,
        },
      })) as any;

      if (res.error || res.status !== 200) {
        throw new Error(res.error || "Failed to update feature");
      }

      // RequestExecutor returns { data: apiResponse, status, error }
      // API response structure: { status: 200, success: true, data: { feature } }
      const apiResponse = res.data;
      
      if (apiResponse && apiResponse.success && apiResponse.data) {
        return {
          status: apiResponse.status || 200,
          response: apiResponse.data,
        } as UpdateFeatureResponse;
      }
      
      // Fallback: if data is already in the expected format
      if (apiResponse && apiResponse.feature) {
        return {
          status: apiResponse.status || 200,
          response: apiResponse,
        } as UpdateFeatureResponse;
      }
      
      throw new Error("Invalid response format");
    } catch (error) {
      captureErrorToSentry(error, "Error in updateFeature");
      throw error;
    }
  }

  async deleteFeature(featureId: string): Promise<DeleteFeatureResponse> {
    try {
      const token = await this.getToken();
      const path = `${getGrowthEndpoints()}/serviceScreenFeaturesApiFunctions-deleteFeature`;
      const res = (await RequestExecutor.fetch({
        apiPath: path,
        apiMethod: HTTPMethods.POST,
        requestHeader: {
          Authorization: token,
        },
        requestBody: {
          featureId,
        },
      })) as any;

      if (res.error || res.status !== 200) {
        throw new Error(res.error || "Failed to delete feature");
      }

      // RequestExecutor returns { data: apiResponse, status, error }
      // API response structure: { status: 200, success: true, data: { success } }
      const apiResponse = res.data;
      
      if (apiResponse && apiResponse.success && apiResponse.data) {
        return {
          status: apiResponse.status || 200,
          response: apiResponse.data,
        } as DeleteFeatureResponse;
      }
      
      // Fallback: if data is already in the expected format
      if (apiResponse && (apiResponse.success !== undefined || apiResponse.response)) {
        return {
          status: apiResponse.status || 200,
          response: apiResponse.response || { success: true },
        } as DeleteFeatureResponse;
      }
      
      throw new Error("Invalid response format");
    } catch (error) {
      captureErrorToSentry(error, "Error in deleteFeature");
      throw error;
    }
  }

  async bulkUploadFeatures(
    fileUrl: string
  ): Promise<BulkUploadFeaturesResponse> {
    try {
      const token = await this.getToken();
      const path = `${getGrowthEndpoints()}/serviceScreenFeaturesApiFunctions-bulkUploadFeatures`;
      const res = (await RequestExecutor.fetch({
        apiPath: path,
        apiMethod: HTTPMethods.POST,
        requestHeader: {
          Authorization: token,
        },
        requestBody: {
          fileUrl,
        },
      })) as any;

      if (res.error || res.status !== 200) {
        throw new Error(res.error || "Failed to bulk upload features");
      }

      // RequestExecutor returns { data: apiResponse, status, error }
      // API response structure: { status: 200, success: true, data: { successCount, errorCount, ... } }
      const apiResponse = res.data;
      
      if (apiResponse && apiResponse.success && apiResponse.data) {
        return {
          status: apiResponse.status || 200,
          response: apiResponse.data,
        } as BulkUploadFeaturesResponse;
      }
      
      // Fallback: if data is already in the expected format
      if (apiResponse && (apiResponse.successCount !== undefined || apiResponse.response)) {
        return {
          status: apiResponse.status || 200,
          response: apiResponse.response || apiResponse,
        } as BulkUploadFeaturesResponse;
      }
      
      throw new Error("Invalid response format");
    } catch (error) {
      captureErrorToSentry(error, "Error in bulkUploadFeatures");
      throw error;
    }
  }

  async downloadFeaturesList(): Promise<DownloadFeaturesListResponse> {
    try {
      const token = await this.getToken();
      const path = `${getGrowthEndpoints()}/serviceScreenFeaturesApiFunctions-downloadFeaturesList`;
      const res = (await RequestExecutor.fetch({
        apiPath: path,
        apiMethod: HTTPMethods.POST,
        requestHeader: {
          Authorization: token,
        },
        requestBody: {},
      })) as any;

      if (res.error || res.status !== 200) {
        throw new Error(res.error || "Failed to download features list");
      }

      // RequestExecutor returns { data: apiResponse, status, error }
      // API response structure: { status: 200, success: true, data: { downloadUrl } }
      const apiResponse = res.data;
      
      if (apiResponse && apiResponse.success && apiResponse.data) {
        return {
          status: apiResponse.status || 200,
          response: apiResponse.data,
        } as DownloadFeaturesListResponse;
      }
      
      // Fallback: if data is already in the expected format
      if (apiResponse && (apiResponse.downloadUrl || apiResponse.response)) {
        return {
          status: apiResponse.status || 200,
          response: apiResponse.response || apiResponse,
        } as DownloadFeaturesListResponse;
      }
      
      throw new Error("Invalid response format");
    } catch (error) {
      captureErrorToSentry(error, "Error in downloadFeaturesList");
      throw error;
    }
  }

  async getAllFeaturesInfo(): Promise<{
    status: number;
    success: boolean;
    data: Array<{
      featureName: string;
      storeIds: string[];
      excludedStoreIds: string[];
      launchType: string;
    }>;
  }> {
    try {
      const token = await this.getToken();
      const path = `${getGrowthEndpoints()}/releaseJetApiFunctions-getAllFeaturesInfo`;
      const res = (await RequestExecutor.fetch({
        apiPath: path,
        apiMethod: HTTPMethods.POST,
        requestHeader: {
          Authorization: token,
        },
        requestBody: {},
      })) as any;

      if (res.error || res.status !== 200) {
        throw new Error(res.error || "Failed to fetch features info");
      }

      const apiResponse = res.data;

      if (apiResponse && apiResponse.success && apiResponse.data) {
        return {
          status: apiResponse.status || 200,
          success: apiResponse.success,
          data: apiResponse.data,
        };
      }

      if (apiResponse && Array.isArray(apiResponse.data)) {
        return {
          status: apiResponse.status || 200,
          success: apiResponse.success || true,
          data: apiResponse.data,
        };
      }

      throw new Error("Invalid response format");
    } catch (error) {
      captureErrorToSentry(error, "Error in getAllFeaturesInfo");
      throw error;
    }
  }
}

